import { ActionContext } from 'vuex';
import { ActionsImpl } from 'direct-vuex';
import { rootActionContext } from '@/store';
import Simulador from '@/plugins/api/Simulador';
import Tabela from '@/plugins/api/Tabela';
import Fipe from '@/plugins/api/Fipe';
import Placa from '@/plugins/api/Placa';
import Vendedor from '@/plugins/api/Vendedor';
import VendedorStorage from '@/plugins/VendedorStorage';

interface StoreActions extends ActionsImpl {
  LOAD_COTACAO(store: ActionContext<StoreState, any>, payload: number): Promise<any>;

  SET_VENDEDOR(store: ActionContext<StoreState, any>, payload: number): Promise<any>;

  LOAD_MARCAS(store: ActionContext<StoreState, any>): Promise<any>;

  SET_PLACA(store: ActionContext<StoreState, any>, payload: string): Promise<any>;

  SET_FIPE(store: ActionContext<StoreState, any>, payload: string): Promise<any>;

  SET_MARCA(store: ActionContext<StoreState, any>, payload: { id: number, name: string }): Promise<any>;

  SET_MODELO(store: ActionContext<StoreState, any>, payload: { id: number, name: string }): Promise<any>;

  SET_ANO(store: ActionContext<StoreState, any>, payload: { id: string, name: string }): Promise<any>;

  FROM_COD_ANO(store: ActionContext<StoreState, any>, payload: DataLandingPage): Promise<any>;

  GET_VALOR_TABELA(store: ActionContext<StoreState, any>): Promise<any>;

  ATUALIZAR_COBERTURA(
    store: ActionContext<StoreState, any>,
    payload: { label: string, value: number, sqid: string },
  ): Promise<any>;

  VALIDAR_DADOS(store: ActionContext<StoreState, any>, payload: boolean): Promise<any>;

  VALIDAR_DADOS_FORM(store: ActionContext<StoreState, any>): Promise<any>;
}

const actions = {
  async LOAD_COTACAO(context, payload) {
    const { commit } = rootActionContext(context);

    commit.SET_LOADING_COTACAO(true);
    try {
      const response = await Simulador.get(payload);
      commit.SET_COTACAO(response.data);
    } finally {
      commit.SET_LOADING_COTACAO(false);
    }
  },
  async SET_VENDEDOR(context, payload) {
    const { commit } = rootActionContext(context);

    const response = await Vendedor.get(payload);
    commit.SET_VENDEDOR(response.data);
    if (response.data) {
      VendedorStorage.set(response.data.id);
    }
  },
  async LOAD_MARCAS(context) {
    const { commit } = rootActionContext(context);

    commit.SET_LOADING_MARCAS(true);
    const data = await Fipe.getMarcas();
    commit.SET_LISTA_MARCAS(data);
    commit.SET_LOADING_MARCAS(false);
  },
  async SET_PLACA(context, payload) {
    const { commit, dispatch } = rootActionContext(context);

    commit.SET_PLACA(payload);
    commit.SET_PLACA_NAO_ENCONTRADA(false);
    if (payload.length === 7) {
      commit.SET_LOADING_PLACA(true);

      try {
        const data = await Placa.get(payload);

        const anos = data.dados_do_veiculo.ano.split('/') as string[];
        const modeloAno = anos[anos.length - 1];
        commit.SET_ANO({ id: `${modeloAno}`, name: modeloAno });
        commit.SET_LISTA_PLACA_MODELOS(data.fipes);

        if (data.fipes.length === 0) {
          throw new Error();
        } else if (data.fipes.length === 1) {
          const fipe = data.fipes[0].codigo;
          dispatch.SET_FIPE(fipe);
        }
      } catch (error) {
        commit.SET_PLACA_NAO_ENCONTRADA(true);
        commit.SET_LISTA_PLACA_MODELOS([]);
      } finally {
        commit.SET_LOADING_PLACA(false);
      }
    }
  },
  async SET_FIPE(context, payload) {
    const { state, commit, dispatch } = rootActionContext(context);

    commit.SET_CODIGO_FIPE(payload);
    commit.SET_PLACA_NAO_ENCONTRADA(false);
    commit.SET_LOADING_PLACA(true);
    try {
      const data = await Fipe.getFipe(payload);
      const fipe = data.find((opt: FipeVeiculo) => opt.ano === state.cotacao.ano);

      if (fipe) {
        const marca = parseInt(fipe.id_marca, 10);
        const ano = parseInt(fipe.id_modelo_ano, 10);
        const preco = fipe.preco.replace('R$', '').replace('.', '').replace(',', '.').trim();

        commit.SET_VEICULO_TIPO(fipe.tipo);
        commit.SET_MARCA({ id: marca, name: fipe.marca });
        commit.SET_MODELO({ id: ano, name: fipe.name });
        commit.SET_COMBUSTIVEL(fipe.combustivel);
        commit.SET_VALOR_FIPE(parseFloat(preco));

        // Mantém o botão de prosseguir desabilitado (loading_fipe) até que
        // valor_mensalidade/valor_adesao sejam calculados, evitando que o
        // formulário seja enviado antes desses valores existirem.
        commit.SET_LOADING_FIPE(true);
        try {
          await dispatch.GET_VALOR_TABELA();
          commit.SET_LOADED_FIPE(true);
        } finally {
          commit.SET_LOADING_FIPE(false);
        }
      } else {
        throw new Error('');
      }
    } catch (error) {
      commit.SET_LISTA_PLACA_MODELOS([]);
    } finally {
      commit.SET_LOADING_PLACA(false);
    }
  },
  async SET_MARCA(context, payload) {
    const { state, commit } = rootActionContext(context);

    commit.SET_LOADING_MODELOS(true);
    commit.SET_MARCA(payload);
    commit.SET_LISTA_MODELOS([]);
    commit.SET_LISTA_ANOS([]);

    const marca = state.simulacao.marca_id as number;
    const data = await Fipe.getModelos(marca);

    commit.SET_LISTA_MODELOS(data);
    commit.SET_LOADING_MODELOS(false);
  },
  async SET_MODELO(context, payload) {
    const { state, commit } = rootActionContext(context);

    commit.SET_LOADING_ANOS(true);
    commit.SET_MODELO(payload);
    commit.SET_LISTA_ANOS([]);

    const marca = state.simulacao.marca_id as number;
    const modelo = state.simulacao.modelo_id as number;
    const data = await Fipe.getAnos(marca, modelo);

    data.forEach((value, index) => {
      const val = value.name.toString();
      data[index].label = val === '32000' ? '0 KM' : val;
    });

    commit.SET_LISTA_ANOS(data);
    commit.SET_LOADING_ANOS(false);
  },
  async SET_ANO(context, payload) {
    const { state, commit, dispatch } = rootActionContext(context);

    commit.SET_LOADING_FIPE(true);
    commit.SET_ANO(payload);

    const marca = state.simulacao.marca_id as number;
    const modelo = state.simulacao.modelo_id as number;
    const ano = state.simulacao.ano_id as string;

    try {
      const data = await Fipe.getVeiculo(marca, modelo, ano);
      commit.SET_CODIGO_FIPE(data.fipe_codigo);

      const preco = parseFloat(
        data.preco.replace('R$', '').split('.').join('').replace(',', '.'),
      );
      commit.SET_VALOR_FIPE(preco);
      commit.SET_COMBUSTIVEL(data.combustivel);

      await dispatch.GET_VALOR_TABELA();
      commit.SET_LOADED_FIPE(true);
    } finally {
      commit.SET_LOADING_FIPE(false);
    }
  },
  async FROM_COD_ANO(context, payload) {
    const { state, commit, dispatch } = rootActionContext(context);

    // Evita que consulte os dados novamente caso a pessoa volte a tela inicial
    if (!state.origem_fipe) {
      commit.SET_ORIGEM_FIPE(true);
      commit.SET_CODIGO_FIPE(payload.fipe!);
      commit.SET_ANO({ id: `${payload.ano}`, name: payload.ano! });
      commit.SET_PLACA(payload.placa!);

      // Mantém o botão de prosseguir desabilitado (loading_fipe) durante
      // toda a busca, incluindo o cálculo de valor_mensalidade/valor_adesao,
      // evitando que o formulário seja enviado antes desses valores existirem.
      commit.SET_LOADING_FIPE(true);
      try {
        const data = await Fipe.getFipe(payload.fipe!);

        const selected = data.find(
          (item) => `${item.ano} ${item.combustivel}` === state.simulacao.ano_id,
        );

        if (selected) {
          const idMarca = parseInt(selected.id_marca, 10);
          const idModelo = parseInt(selected.id_modelo, 10);
          const idAno = selected.id_modelo_ano;

          commit.SET_MARCA({ id: idMarca, name: selected.marca });
          commit.SET_MODELO({ id: idModelo, name: selected.name });
          commit.SET_ANO({ id: idAno, name: `${selected.ano} ${selected.combustivel}` });

          const preco = parseFloat(
            selected.preco.replace('R$', '').split('.').join('').replace(',', '.'),
          );

          commit.SET_VALOR_FIPE(preco);
          commit.SET_COMBUSTIVEL(selected.combustivel);

          await dispatch.GET_VALOR_TABELA();
          commit.SET_LOADED_FIPE(true);
        }
      } finally {
        commit.SET_LOADING_FIPE(false);
      }
    }
  },
  async GET_VALOR_TABELA(context) {
    const { state, commit } = rootActionContext(context);

    let valor = state.cotacao.valor_fipe;
    const requestData = {
      marca: state.cotacao.marca,
      modelo: state.cotacao.modelo,
      ano: state.cotacao.ano,
      fipe_codigo: state.cotacao.fipe_codigo,
      valor,
    };
    const data = await Tabela.get(requestData);

    let mensalParcial = data.mensalidade;
    if (valor <= 110000) {
      valor = 110000;
      mensalParcial = state.configuracoes.abaixo_110;
    }
    commit.SET_VALOR_PLANO(mensalParcial);
    commit.SET_VALOR_ADESAO(data.adesao);
    return data;
  },
  async ATUALIZAR_COBERTURA(context, payload) {
    const { state, commit } = rootActionContext(context);

    commit.SET_COBERTURA(payload);

    if (!state.cotacao.id) {
      return;
    }

    const dados = {
      id: state.cotacao.id,
      nome: state.cotacao.nome,
      email: state.cotacao.email,
      ddd: state.cotacao.ddd,
      telefone: state.cotacao.telefone.replace(/\D/g, ''),
      marca: state.cotacao.marca,
      modelo: state.cotacao.modelo,
      ano: state.cotacao.ano,
      fipe_codigo: state.cotacao.fipe_codigo,
      valor_fipe: state.cotacao.valor_fipe,
      cobertura_sqid: state.cotacao.cobertura_sqid,
    };

    const data = await Simulador.update(dados);
    if (data.option) {
      // Veículos com FIPE <= 110 mil têm mensalidade fixa promocional
      // (state.configuracoes.abaixo_110); não sobrescrever com o valor
      // calculado por veículo.
      if (state.cotacao.valor_fipe > 110000) {
        commit.SET_VALOR_MENSAL(data.option.monthly_value);
      }
      commit.SET_VALOR_ADESAO(data.option.fee_value);
    }
  },
  async VALIDAR_DADOS(context, payload) {
    const { state, commit } = rootActionContext(context);

    if (payload) {
      commit.ATIVAR_VALIDACAO();
    }
    if (state.validacao) {
      const errors: DadosErrors = {};
      if (state.cotacao.nome.length < 1) {
        errors.nome = true;
      }
      if (state.cotacao.ddd.length < 2) {
        errors.ddd = true;
      }
      if (state.cotacao.telefone.length < 9) {
        errors.telefone = true;
      }
      if (!state.loaded_fipe) {
        errors.fipe = true;
      }
      if (!state.simulacao.marca_id) {
        errors.marca = true;
      }
      if (!state.simulacao.modelo_id) {
        errors.modelo = true;
      }
      if (state.simulacao.ano_id === '') {
        errors.ano = true;
      }

      commit.SET_ERRORS(errors);

      if (Object.keys(errors).length > 0) {
        throw new Error();
      }
    }
  },
  async VALIDAR_DADOS_FORM(context) {
    const { state, commit } = rootActionContext(context);

    const errors: DadosErrors = {};
    if (state.cotacao.tipo !== 3) {
      errors.tipo = true;
    }
    if (state.pesquisa_placa) {
      if (state.cotacao.placa.length !== 7) {
        errors.placa = true;
      }
      if (state.cotacao.fipe_codigo === '') {
        errors.modelo = true;
      }
    } else {
      if (!state.simulacao.marca_id) {
        errors.marca = true;
      }
      if (!state.simulacao.modelo_id) {
        errors.modelo = true;
      }
      if (state.simulacao.ano_id === '') {
        errors.ano = true;
      }
    }

    commit.SET_ERRORS(errors);

    if (Object.keys(errors).length > 0) {
      throw new Error();
    }
  },
} as StoreActions;

export default actions;
